import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { type ButtonHTMLAttributes, forwardRef } from 'react'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  asChild?: boolean
}

const variants = {
  primary:
    'bg-accent text-white font-semibold hover:bg-accent-hover shadow-[0_0_20px_var(--color-accent-glow)] hover:shadow-[0_0_28px_var(--color-accent-glow)] active:shadow-[0_0_12px_var(--color-accent-glow)]',
  secondary:
    'glass-interactive text-text-primary hover:text-text-primary',
  outline:
    'bg-transparent border border-border text-text-secondary hover:border-border-hover hover:bg-bg-hover hover:text-text-primary',
  ghost:
    'bg-transparent text-text-secondary hover:bg-bg-hover hover:text-text-primary',
  destructive:
    'bg-error-muted text-error hover:bg-[rgba(220,38,38,0.18)] dark:hover:bg-[rgba(248,113,113,0.20)] active:bg-[rgba(220,38,38,0.22)] dark:active:bg-[rgba(248,113,113,0.25)]',
}

const sizes = {
  sm: 'h-8 px-3.5 text-xs gap-1.5 rounded-[var(--radius-sm)]',
  md: 'h-9 px-5 text-sm gap-2 rounded-[var(--radius-sm)]',
  lg: 'h-11 px-6 text-sm gap-2 rounded-[var(--radius-md)]',
  icon: 'h-9 w-9 rounded-[var(--radius-sm)]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ring-offset',
          'disabled:pointer-events-none disabled:opacity-40',
          'cursor-pointer select-none',
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'
